import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";
import { Call, LocationOn } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { addComment, getoneProduct } from "../redux/slices/product/thunk";
import LoadingComponent from "../components/LoadingComponent";
import { addToCart } from "../redux/slices/cart/thunk";

const ServiceDetailsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { error, loading, product } = useSelector(
    (state) => state.productSlice
  );
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [selectedQty, setSelectedQty] = useState(1);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [userHasCommented, setUserHasCommented] = useState(false);
  const [addToCartLoading, setAddToCartLoading] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getoneProduct({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (product) {
      setComments(Array.isArray(product.comments) ? product.comments : []);

      if (user && product.comments) {
        const hasCommented = product.comments.some(
          (comment) => comment.user?._id === user._id
        );
        setUserHasCommented(hasCommented);
      }
    }
  }, [product, user]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !user) return;

    const tempComment = {
      _id: `temp_${Date.now()}`,
      user: {
        _id: user._id,
        userName: user.name || "Anonymous",
      },
      comment: newComment.trim(),
      createdAt: new Date().toISOString(),
      isPending: true,
    };

    setComments((prev) => [...prev, tempComment]);
    setNewComment("");
    setUserHasCommented(true);

    try {
      const data = {
        userId: user._id,
        productId: id,
        comment: newComment.trim(),
      };

      const result = await dispatch(addComment({ data }));
      

      // تحديث التعليق بعد الاستجابة من السيرفر
      if (result.payload && result.payload.comment) {
        setComments((prev) =>
          prev.map((c) =>
            c._id === tempComment._id ? result.payload.comment : c
          )
        );
      }
    } catch (err) {
      setComments((prev) => prev.filter((c) => c._id !== tempComment._id));
      setUserHasCommented(false);
    } finally{
      await dispatch(getoneProduct({id}))
    }
  };

  const handleIncreaseQty = () => {
    if (!product) return;
    if (!product?.stock_limit) {
      setSelectedQty((prev) => prev + 1);
    } else {
      if (selectedQty < product.count_in_stock) {
        setSelectedQty((prev) => prev + 1);
      }
    }
  };

  const handleDecreaseQty = () => {
    if (selectedQty > 1) {
      setSelectedQty((prev) => prev - 1);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    if (product?.stock_limit && selectedQty > product?.availableQuantity) {
      return;
    }

    setAddToCartLoading(true);

    const data = {
      familyId: product?.familyId?._id,
      productId: product._id,
      userId: user?._id,
      quantity: selectedQty,
    };

    try {
      await dispatch(addToCart({ data }));
    } catch (error) {
      console.error("Add to cart error:", error);
    } finally {
      // Clear loading state for this product
      setAddToCartLoading(false);
    }
  };

  const handleContactPhone = () => {
    if (product?.familyId?.phoneNumber) {
      const phone = product?.familyId?.phoneNumber;
      window.location.href = `tel:${phone}`;
    } else {
      setOpenContactDialog(true);
    }
  };

  const handleSendMessage = () => {
    console.log(
      "Send message to family:",
      product?.familyId?._id,
      contactMessage
    );
    setOpenContactDialog(false);
    setContactMessage("");
  };

    if (user?.type !== "client") {
      return <Navigate to="/" replace />;
    }
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <LoadingComponent />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5">{t("common.notFound")}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        minHeight: "100vh",
        py: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 2, md: 3 } }}>
        {/* زر العودة */}
        <Button
          startIcon={<ArrowBackIcon sx={{ mx: 2 }} />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2, textTransform: "none" }}
        >
          {t("common.back")}
        </Button>

        {/* بطاقة الخدمة */}
        <Card
          elevation={3}
          sx={{ borderRadius: 2, mb: 3, position: "relative" }}
        >
          <Box sx={{ position: "relative", width: "100%", pt: "56.25%" }}>
            <Box
              component="img"
              src={product?.image}
              alt={product?.name}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.5s",
                "&:hover": { transform: "scale(1.05)" },
              }}
            />
          </Box>
          {product?.discount > 0 && (
            <Chip
              label={`${product?.discount}% OFF`}
              color="error"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                fontWeight: "bold",
                fontSize: "0.8rem",
              }}
            />
          )}
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {product?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {product?.description}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {product?.activityDescription}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  color: product?.discount > 0 ? "error.main" : "text.primary",
                  fontWeight: 700,
                }}
              >
                {product?.discount > 0
                  ? (
                      product?.price -
                      (product?.price * product?.discount) / 100
                    )?.toFixed(2)
                  : product.price.toFixed(2)}{" "}
                <span>{t("rs")}</span>
              </Typography>
              {product.discount > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: "line-through",
                    color: "text.secondary",
                  }}
                >
                  {product.price.toFixed(2)} <span>{t("rs")}</span>
                </Typography>
              )}
            </Box>
            {product.stock_limit && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("common.availableQty")} :{" "}
                <span>{product.count_in_stock}</span>
              </Typography>
            )}
            {!product.stock_limit && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("common.unlimitedNote")}
              </Typography>
            )}

            {/* كمية الاختيار */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <IconButton
                onClick={handleDecreaseQty}
                disabled={selectedQty <= 1}
                size="medium"
              >
                <RemoveIcon />
              </IconButton>
              <Box sx={{ minWidth: 32, textAlign: "center" }}>
                <Typography variant="body1">{selectedQty}</Typography>
              </Box>
              <IconButton
                onClick={handleIncreaseQty}
                disabled={
                  product.stock_limit && selectedQty >= product.count_in_stock
                }
                size="small"
              >
                <AddIcon />
              </IconButton>
            </Box>
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2, pt: 0, display: "flex", gap: 2 }}>
            {/* زر الاتصال */}
            <Button
              variant="contained"
              color="primary"
              sx={{ flex: 1, py: 1 }}
              onClick={handleContactPhone}
              startIcon={<Call sx={{ mx: 2 }} />}
            >
              {t("common.contactFamily")}
            </Button>
            {/* زر إضافة إلى السلة مع الكمية */}
            <Button
              variant="outlined"
              color="primary"
              sx={{ flex: 1, py: 1 }}
              onClick={handleAddToCart}
              startIcon={
                addToCartLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AddShoppingCartIcon sx={{ml:1}} />
                )
              }
              disabled={addToCartLoading}
            >
              {t("common.addToCart")} ({selectedQty})
            </Button>
          </CardActions>
        </Card>

        {/* معلومات العائلة */}
   {product?.familyId && (
  <Paper elevation={1} sx={{ borderRadius: 2, p: { xs: 2, md: 3 }, mb: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
      {t("common.postedBy")} : {product.familyId.userName}
    </Typography>
    <Divider sx={{ my: 1 }} />

    {product.familyId.phoneNumber && (
      <>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <PhoneIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {product.familyId.phoneNumber}
          </Typography>
        </Stack>
        {/* <Divider sx={{ my: 1 }} /> */}
      </>
    )}

    {product.familyId.address && (
      <>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <HomeIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {product.familyId.address}
          </Typography>
        </Stack>
        {/* <Divider sx={{ my: 1 }} /> */}
      </>
    )}

    {product.familyId.description && (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {t("userForm.fields.description")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {product.familyId.description}
        </Typography>
        <Divider sx={{ my: 1 }} />
      </>
    )}

    {product.familyId.activityDescription && (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {t("userForm.fields.activityDescription")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {product.familyId.activityDescription}
        </Typography>
        <Divider sx={{ my: 1 }} />
      </>
    )}

    {product.familyId.location && (
      <>
        <Stack direction="row" alignItems="center" spacing={2}>
          <LocationOn fontSize="small" color="action" />
          <Typography
            variant="body2"
            color="primary"
            component="a"
            href={product.familyId.location}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: "none" }}
          >
            {t("userForm.fields.location")}
          </Typography>
        </Stack>
        {/* <Divider sx={{ my: 1 }} /> */}
      </>
    )}
  </Paper>
)}


        {/* التعليقات */}
        <Paper
          elevation={1}
          sx={{ borderRadius: 2, p: { xs: 2, md: 3 }, mb: 3 }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("common.comments")}
          </Typography>

          {userHasCommented && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("common.youHaveCommented")}
            </Alert>
          )}

          {comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("common.noComments")}
            </Typography>
          ) : (
            <List sx={{ maxHeight: 300, overflow: "auto" }}>
              {comments.map((c) => (
                <React.Fragment key={c._id}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar>{c?.user?.userName?.charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {c?.user?.userName}
                            {c.isPending && " (جارٍ الإرسال...)"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c?.createdAt?.split("T")[0]}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{ display: "flex", justifyContent: "start" }}
                        >
                          {c.comment}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          )}

          {!userHasCommented && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder={t("common.writeComment")}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && newComment.trim())
                    handleAddComment();
                }}
              />
              <Button
                variant="contained"
                endIcon={<SendIcon sx={{ mx: 1 }} />}
                onClick={handleAddComment}
                disabled={!newComment.trim()}
              >
                {t("common.addComment")}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Dialog للاتصال الداخلي إذا بدون رقم */}
      <Dialog
        open={openContactDialog}
        onClose={() => setOpenContactDialog(false)}
      >
        <DialogTitle>
          {t("common.sendMessageTo", {
            name: product?.familyId?.userName || t("common.family"),
          })}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t("common.yourMessage")}
            multiline
            rows={4}
            fullWidth
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContactDialog(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!contactMessage.trim()}
          >
            {t("common.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceDetailsPage;
